import React, { useState, useEffect, useRef } from 'react';
import { format, parse, addDays, subDays, isSameDay, isValid } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import { useFootballStates } from '@/hooks/useFootballStates';
import LoadingState from '@/components/football/LoadingState';
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent,
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableRow 
} from "@/components/ui/table";
import { useLocation } from 'react-router-dom';
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from '@tanstack/react-query';
import { toast } from "sonner";

interface Country {
  id: number;
  name: string;
  display_order: number;
}

interface League {
  id: number;
  name: string;
  country: string;
  display_order: number;
}

interface Match {
  HomeTeam: string;
  AwayTeam: string;
  Date: string;
  FTHG: string;
  FTAG: string;
  HTHG: string;
  HTAG: string;
  HS: string;
  AS: string;
  HST: string;
  AST: string;
  HF: string;
  AF: string;
  HC: string;
  AC: string;
  HY: string;
  AY: string;
  HR: string;
  AR: string;
  [key: string]: any;
}

const Results = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { savedStates, isLoading } = useFootballStates();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [matchesByLeague, setMatchesByLeague] = useState<Record<string, any[]>>({});
  const [dateRange, setDateRange] = useState<Date[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sortedLeagueNames, setSortedLeagueNames] = useState<string[]>([]);
  const [filterByLeague, setFilterByLeague] = useState(false);
  const [filterCountry, setFilterCountry] = useState<string | null>(null);
  const [filterLeague, setFilterLeague] = useState<string | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<{match: Match, leagueName: string, index: number} | null>(null);
  const shouldFindLatestDate = useRef(false);
  const initialFilterApplied = useRef(false);
  
  // Fetch countries and leagues for ordering
  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .order('display_order');
      
      if (error) throw error;
      return data as Country[];
    },
  });

  const { data: leagues } = useQuery({
    queryKey: ['leagues'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leagues')
        .select('*')
        .order('display_order');
      
      if (error) throw error;
      return data as League[];
    },
  });
  
  // Check for league filter in location state
  useEffect(() => {
    console.log("Location state changed:", location.state);
    
    if (location.state?.filterByLeague) {
      console.log("Setting filter by league:", location.state);
      setFilterByLeague(true);
      setFilterCountry(location.state.country || null);
      setFilterLeague(location.state.league || null);
      initialFilterApplied.current = true;
      
      if (location.state?.loadLatestForLeague) {
        shouldFindLatestDate.current = true;
      }
    }
    
    // Check if we should load the latest date with matches
    if (location.state?.findLatestDate || location.state?.loadLatest) {
      shouldFindLatestDate.current = true;
    }
    
    // Clear the state to prevent reloading on subsequent renders
    if (location.state) {
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  
  // Generate a range of dates to display (14 days)
  useEffect(() => {
    const range: Date[] = [];
    const startDate = subDays(currentDate, 7);
    
    for (let i = 0; i < 14; i++) {
      range.push(addDays(startDate, i));
    }
    
    setDateRange(range);
  }, [currentDate]);
  
  // Find all available match dates across all data
  const getAllMatchDates = () => {
    if (!savedStates || savedStates.length === 0) return [];
    
    const matchDates: Date[] = [];
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    console.log('Finding all match dates...');
    
    savedStates.forEach(state => {
      // Skip if filtered and not matching the filter
      if (filterByLeague && (
          state.country !== filterCountry || 
          state.league !== filterLeague
      )) {
        return;
      }
      
      if (!state.data || !Array.isArray(state.data)) return;
      
      console.log(`Checking ${state.data.length} matches in ${state.country} - ${state.league} - ${state.year}`);
      
      state.data.forEach((match, index) => {
        try {
          const matchDate = match.Date;
          console.log(`Match ${index}: Date = "${matchDate}", Type = ${typeof matchDate}`);
          if (!matchDate) return;
          
          // Parse the date with more detailed logging
          let parsedDate: Date | null = null;
          
          if (typeof matchDate === 'string') {
            if (matchDate.includes('-')) {
              // Handle DD-MM-YYYY or DD-MM-YY format
              const parts = matchDate.split('-');
              if (parts.length === 3) {
                const day = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1; // JS months are 0-indexed
                let year = parseInt(parts[2]);
                
                // Handle 2-digit years
                if (year < 100) {
                  year += 2000; // Convert YY to 20YY
                }
                
                if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                  parsedDate = new Date(year, month, day);
                }
              }
            } else if (matchDate.includes('/')) {
              // Handle D/M/YY or DD/MM/YY format
              const parts = matchDate.split('/');
              if (parts.length === 3) {
                const day = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1;
                let year = parseInt(parts[2]);
                
                // Handle 2-digit years
                if (year < 100) {
                  year += 2000; // Convert YY to 20YY
                }
                
                if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                  parsedDate = new Date(year, month, day);
                }
              }
            } else {
              // Try standard date parsing
              parsedDate = new Date(matchDate);
            }
          }
          
          // Verify the parsed date is valid
          if (parsedDate && isValid(parsedDate)) {
            // Include all dates - remove the future date filter since this is causing issues
            matchDates.push(parsedDate);
          }
        } catch (e) {
          console.error('Error parsing match date:', e, 'from value:', match.Date);
        }
      });
    });
    
    // Remove duplicates and sort
    const uniqueDates = Array.from(
      new Set(matchDates.map(date => date.toISOString()))
    ).map(dateStr => new Date(dateStr));
    
    // Sort by date (newest first)
    uniqueDates.sort((a, b) => b.getTime() - a.getTime());
    
    // Debugging - log all found match dates
    console.log(`Found ${uniqueDates.length} unique match dates`);
    if (uniqueDates.length > 0) {
      console.log('Latest 5 match dates:', uniqueDates.slice(0, 5).map(d => format(d, 'yyyy-MM-dd')));
    } else {
      console.log('No match dates found');
    }
    
    return uniqueDates;
  };
  
  // Extract all match dates for the current filter
  const getAllMatchDatesForFilter = () => {
    return getAllMatchDates();
  };
  
  // Find the most recent date with matches
  const getLatestDateWithMatches = () => {
    const dates = getAllMatchDates();
    
    if (dates.length > 0) {
      const latestDate = dates[0];
      console.log('Latest match date found:', format(latestDate, 'yyyy-MM-dd'));
      return latestDate;
    } else {
      console.log('No match dates found, using current date');
      return new Date();
    }
  };
  
  // Find matches for the selected date and group them by league
  useEffect(() => {
    if (!savedStates || savedStates.length === 0) return;
    
    const matches: Record<string, any[]> = {};
    const currentDateString = format(currentDate, 'dd-MM-yyyy');
    
    console.log("Finding matches for date:", currentDateString);
    console.log("Filter by league:", filterByLeague);
    console.log("Filter country:", filterCountry);
    console.log("Filter league:", filterLeague);
    
    let matchFound = false;
    
    savedStates.forEach(state => {
      // Apply league filter if active
      if (filterByLeague && (
        state.country !== filterCountry || 
        state.league !== filterLeague
      )) {
        return;
      }
      
      if (!state.data || !Array.isArray(state.data)) return;
      
      const matchesOnDate = state.data.filter(match => {
        // Handle different date formats
        try {
          const matchDate = match.Date;
          if (!matchDate) return false;
          
          // Try to parse various date formats
          let parsedDate;
          if (matchDate.includes('-')) {
            // Handle dd-MM-yyyy or dd-MM-yy format
            const [day, month, year] = matchDate.split('-');
            let fullYear = Number(year);
            if (fullYear < 100) {
              fullYear += 2000; // Convert YY to 20YY
            }
            parsedDate = new Date(fullYear, Number(month) - 1, Number(day));
          } else if (matchDate.includes('/')) {
            // Handle D/M/YY or DD/MM/YY format
            const [day, month, year] = matchDate.split('/');
            let fullYear = Number(year);
            if (fullYear < 100) {
              fullYear += 2000; // Convert YY to 20YY
            }
            parsedDate = new Date(fullYear, Number(month) - 1, Number(day));
          } else {
            // Try standard date parsing
            parsedDate = new Date(matchDate);
          }
          
          return format(parsedDate, 'dd-MM-yyyy') === currentDateString;
        } catch (e) {
          console.error('Error parsing date:', e);
          return false;
        }
      });
      
      if (matchesOnDate.length > 0) {
        matchFound = true;
        
        // Format league name - remove first 2 digits if present
        let league = state.league || '';
        if (/^\d{2}/.test(league)) {
          league = league.substring(2).trim();
        }
        
        const leagueName = `${state.country} - ${league}`;
        
        // Add match data with team info
        if (!matches[leagueName]) {
          matches[leagueName] = [];
        }
        
        matches[leagueName].push(...matchesOnDate);
      }
    });
    
    console.log("Matches found:", Object.keys(matches).length);
    
    // If no matches found on this date and we have a filter active, attempt to find matches on nearby dates
    if (!matchFound && filterByLeague && filterCountry && filterLeague && shouldFindLatestDate.current) {
      console.log("No matches found for filtered league on current date. Looking for the latest date with matches...");
      const latestMatchDate = getLatestDateWithMatches();
      
      if (latestMatchDate && !isSameDay(latestMatchDate, currentDate)) {
        console.log("Setting date to latest match date for filtered league:", format(latestMatchDate, 'dd-MM-yyyy'));
        setCurrentDate(latestMatchDate);
        // Only show the toast if we actually changed the date
        toast.info(`Showing latest matches for ${filterCountry} - ${filterLeague}`);
        return; // Exit to prevent redundant processing
      }
    }
    
    setMatchesByLeague(matches);
    
    // Sort league names if we have country data
    if (countries && leagues) {
      sortLeaguesByCountryOrder(matches, countries, leagues);
    }
  }, [savedStates, currentDate, countries, leagues, filterByLeague, filterCountry, filterLeague]);

  // Sort leagues by country display order
  const sortLeaguesByCountryOrder = (
    matchesByLeague: Record<string, any[]>,
    countries: Country[],
    leagues: League[]
  ) => {
    console.log("Sorting leagues by country order");
    
    // Create a map of country names to their display orders
    const countryOrderMap = new Map<string, number>();
    countries.forEach(country => {
      countryOrderMap.set(country.name, country.display_order);
    });
    
    // Create a map for leagues by country and name - for debugging
    console.log("Available leagues in database:", leagues.map(l => `${l.country} - ${l.name} (${l.display_order})`));
    
    // Create a combined lookup for easier league matching
    // This will help with exact and fuzzy matching
    const leagueOrderByCountryAndName = new Map<string, number>();
    leagues.forEach(league => {
      const key = `${league.country} - ${league.name}`.toLowerCase();
      leagueOrderByCountryAndName.set(key, league.display_order);
      
      // Also store without spaces for more flexible matching
      const keyNoSpaces = `${league.country}-${league.name}`.toLowerCase().replace(/\s+/g, '');
      leagueOrderByCountryAndName.set(keyNoSpaces, league.display_order);
    });
    
    // Log available leagues in our matches
    const leagueNames = Object.keys(matchesByLeague);
    console.log("Leagues found in matches:", leagueNames);
    
    // Sort leagues by country first, then by the league's display order
    const sorted = leagueNames.sort((a, b) => {
      const [countryA, leagueA] = a.split(' - ');
      const [countryB, leagueB] = b.split(' - ');
      
      // First compare countries by their display order
      const countryOrderA = countryOrderMap.get(countryA) || Number.MAX_SAFE_INTEGER;
      const countryOrderB = countryOrderMap.get(countryB) || Number.MAX_SAFE_INTEGER;
      
      if (countryOrderA !== countryOrderB) {
        return countryOrderA - countryOrderB;
      }
      
      // Countries are the same, now sort by league display order
      // Try to find the league in our database using the exact combined key
      const keyA = a.toLowerCase();
      const keyB = b.toLowerCase();
      
      let orderA = leagueOrderByCountryAndName.get(keyA);
      let orderB = leagueOrderByCountryAndName.get(keyB);
      
      // If not found by the exact key, try to find by checking if the league name is contained
      // in any of the database leagues for that country
      if (orderA === undefined || orderB === undefined) {
        // Find the best match for leagues in the country
        const countryLeagues = leagues.filter(l => l.country === countryA);
        
        // If we haven't found orderA yet
        if (orderA === undefined) {
          // Try to find the closest matching league
          const matchingLeagueA = countryLeagues.find(l => 
            leagueA.includes(l.name) || l.name.includes(leagueA)
          );
          
          orderA = matchingLeagueA?.display_order || Number.MAX_SAFE_INTEGER;
        }
        
        // If we haven't found orderB yet
        if (orderB === undefined) {
          const countryLeaguesB = leagues.filter(l => l.country === countryB);
          const matchingLeagueB = countryLeaguesB.find(l => 
            leagueB.includes(l.name) || l.name.includes(leagueB)
          );
          
          orderB = matchingLeagueB?.display_order || Number.MAX_SAFE_INTEGER;
        }
      }
      
      // Log the results for troubleshooting
      console.log(`Sorting ${a} (order: ${orderA}) vs ${b} (order: ${orderB}`);
      
      // If we found league orders, compare them
      if (orderA !== orderB) {
        return orderA! - orderB!;
      }
      
      // If everything else fails, sort alphabetically
      return leagueA.localeCompare(leagueB);
    });
    
    console.log("Sorted league names:", sorted);
    setSortedLeagueNames(sorted);
  };
  
  // Handle initialization and data loading
  useEffect(() => {
    if (isLoading || !savedStates || savedStates.length === 0 || initialLoadDone) return;
    
    // If we need to find the latest date with matches
    if (shouldFindLatestDate.current) {
      console.log("Finding latest date with matches");
      const latestDate = getLatestDateWithMatches();
      
      if (latestDate) {
        console.log("Setting date to latest match date:", format(latestDate, 'yyyy-MM-dd'));
        setCurrentDate(latestDate);
        
        // If it's a date far in the past, add a user notification
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);
        
        if (latestDate < oneMonthAgo) {
          toast.info(`Latest available match data is from ${format(latestDate, 'MMMM d, yyyy')}`);
        }
      }
    }
    
    // Mark initialization as complete
    setInitialLoadDone(true);
  }, [isLoading, savedStates, initialLoadDone]);
  
  // Show nearby available match dates when no matches found
  const findNearbyMatchDates = () => {
    const allMatchDates = getAllMatchDatesForFilter();
    
    // Return the 5 most recent dates
    return allMatchDates.slice(0, 5);
  };
  
  const navigateDate = (direction: 'prev' | 'next') => {
    setCurrentDate(direction === 'prev' 
      ? subDays(currentDate, 1) 
      : addDays(currentDate, 1)
    );
  };
  
  const selectDate = (date: Date) => {
    setCurrentDate(date);
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      setCurrentDate(date);
      setCalendarOpen(false);
    }
  };

  const clearLeagueFilter = () => {
    setFilterByLeague(false);
    setFilterCountry(null);
    setFilterLeague(null);
    
    // Reset the flag so we find the latest date for all leagues
    initialFilterApplied.current = false;
    shouldFindLatestDate.current = true;
    setInitialLoadDone(false);
  };

  // Navigate to team details page
  const navigateToTeam = (teamName: string) => {
    navigate(`/team/${encodeURIComponent(teamName)}`);
  };

  // Handle match row click
  const handleMatchClick = (match: Match, leagueName: string, index: number) => {
    if (selectedMatch && 
        selectedMatch.match === match && 
        selectedMatch.leagueName === leagueName && 
        selectedMatch.index === index) {
      // If clicking the same match, close it
      setSelectedMatch(null);
    } else {
      // Otherwise, open the new match details
      setSelectedMatch({ match, leagueName, index });
    }
  };

  // Render match details component - display dates exactly as they are
  const renderMatchDetails = (match: Match) => {
    return (
      <div className="p-4 bg-muted/40 border-t">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm text-gray-600">{match.Date}</span>
          <span className="text-md font-medium">
            {match.HomeTeam} vs {match.AwayTeam}
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h4 className="text-md font-semibold mb-2">Match Stats</h4>
            <div className="space-y-1">
              <p>Full Time: {match.FTHG} - {match.FTAG}</p>
              <p>Half Time: {match.HTHG} - {match.HTAG}</p>
              <p>Corners: {match.HC} - {match.AC}</p>
            </div>
          </div>
          <div>
            <h4 className="text-md font-semibold mb-2">Additional Info</h4>
            <div className="space-y-1">
              <p>Shots: {match.HS} - {match.AS}</p>
              <p>Shots on Target: {match.HST} - {match.AST}</p>
              <p>Fouls: {match.HF} - {match.AF}</p>
              <p>Cards: <span className="px-1">🟨 {match.HY}-{match.AY}</span> <span className="px-1 ml-1">🟥 {match.HR}-{match.AR}</span></p>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  if (isLoading) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-8">
          <LoadingState />
        </div>
      </>
    );
  }
  
  // Find nearby dates with matches if we're showing empty results
  const nearbyMatchDates = Object.keys(matchesByLeague).length === 0 ? findNearbyMatchDates() : [];
  
  return (
    <>
      <Header />
      <div className="container mx-auto py-8 px-4">
        {/* Filter indicator */}
        {filterByLeague && filterCountry && filterLeague && (
          <div className="mb-4 p-3 bg-muted rounded-md flex justify-between items-center">
            <div className="font-medium">
              Viewing results for: <span className="font-bold">{filterCountry} - {filterLeague}</span>
            </div>
            <Button variant="outline" size="sm" onClick={clearLeagueFilter}>
              Show All Leagues
            </Button>
          </div>
        )}
        
        {/* Calendar section */}
        <div className="mb-8">
          <div className="flex items-center justify-center mb-4">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline"
                  className="flex items-center gap-2 font-bold text-lg"
                >
                  {format(currentDate, 'MMMM dd, yyyy')}
                  <CalendarIcon className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar
                  mode="single"
                  selected={currentDate}
                  onSelect={handleCalendarSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="relative flex items-center">
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute left-0 z-10" 
              onClick={() => navigateDate('prev')}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <div className="w-full overflow-x-auto py-2">
              <div className="flex justify-between min-w-[700px] mx-auto">
                {dateRange.map((date, index) => {
                  const isToday = isSameDay(date, new Date());
                  const isSelected = isSameDay(date, currentDate);
                  
                  return (
                    <div key={index} className="text-center px-2">
                      <div className="text-sm font-medium">
                        {format(date, 'EEE')}
                      </div>
                      <Button 
                        variant={isSelected ? "default" : "ghost"}
                        className={`
                          w-10 h-10 p-0 mt-1 font-bold
                          ${isToday ? 'ring-2 ring-primary' : ''}
                        `}
                        onClick={() => selectDate(date)}
                      >
                        {format(date, 'd')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-0 z-10" 
              onClick={() => navigateDate('next')}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>
        </div>
        
        {/* Results section */}
        <div className="space-y-4">
          {Object.keys(matchesByLeague).length > 0 ? (
            sortedLeagueNames.length > 0 ? (
              // Display leagues in the sorted order
              sortedLeagueNames.map(leagueName => (
                <Card key={leagueName} className="overflow-hidden">
                  <div className="bg-primary p-3 text-primary-foreground font-medium flex items-center">
                    <h2 className="text-lg">{leagueName}</h2>
                    <ChevronRight className="ml-auto h-5 w-5" />
                  </div>
                  <CardContent className="p-0">
                    <Table>
                      <TableBody>
                        {matchesByLeague[leagueName].map((match, idx) => (
                          <React.Fragment key={idx}>
                            <TableRow 
                              className={`hover:bg-muted/50 cursor-pointer ${
                                selectedMatch && 
                                selectedMatch.leagueName === leagueName && 
                                selectedMatch.index === idx ? 'bg-muted/30' : ''
                              }`}
                              onClick={() => handleMatchClick(match, leagueName, idx)}
                            >
                              <TableCell 
                                className="w-[40%] py-4 pl-4 text-center hover:text-primary hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToTeam(match.HomeTeam);
                                }}
                              >
                                {match.HomeTeam}
                              </TableCell>
                              <TableCell className="w-[20%] text-center font-bold py-4 whitespace-nowrap">
                                {match.FTHG} - {match.FTAG}
                              </TableCell>
                              <TableCell 
                                className="w-[40%] py-4 pr-4 text-center hover:text-primary hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToTeam(match.AwayTeam);
                                }}
                              >
                                {match.AwayTeam}
                              </TableCell>
                            </TableRow>
                            {selectedMatch && 
                             selectedMatch.leagueName === leagueName && 
                             selectedMatch.index === idx && (
                              <TableRow>
                                <TableCell colSpan={3} className="p-0">
                                  {renderMatchDetails(match)}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))
            ) : (
              Object.entries(matchesByLeague).map(([leagueName, matches]) => (
                <Card key={leagueName} className="overflow-hidden">
                  <div className="bg-primary p-3 text-primary-foreground font-medium flex items-center">
                    <h2 className="text-lg">{leagueName}</h2>
                    <ChevronRight className="ml-auto h-5 w-5" />
                  </div>
                  <CardContent className="p-0">
                    <Table>
                      <TableBody>
                        {matches.map((match, idx) => (
                          <React.Fragment key={idx}>
                            <TableRow 
                              className={`hover:bg-muted/50 cursor-pointer ${
                                selectedMatch && 
                                selectedMatch.leagueName === leagueName && 
                                selectedMatch.index === idx ? 'bg-muted/30' : ''
                              }`}
                              onClick={() => handleMatchClick(match, leagueName, idx)}
                            >
                              <TableCell 
                                className="w-[40%] py-4 pl-4 text-center hover:text-primary hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToTeam(match.HomeTeam);
                                }}
                              >
                                {match.HomeTeam}
                              </TableCell>
                              <TableCell className="w-[20%] text-center font-bold py-4 whitespace-nowrap">
                                {match.FTHG} - {match.FTAG}
                              </TableCell>
                              <TableCell 
                                className="w-[40%] py-4 pr-4 text-center hover:text-primary hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToTeam(match.AwayTeam);
                                }}
                              >
                                {match.AwayTeam}
                              </TableCell>
                            </TableRow>
                            {selectedMatch && 
                             selectedMatch.leagueName === leagueName && 
                             selectedMatch.index === idx && (
                              <TableRow>
                                <TableCell colSpan={3} className="p-0">
                                  {renderMatchDetails(match)}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))
            )
          ) : (
            <div className="text-center py-10">
              <p className="text-xl text-muted-foreground">
                No matches found for {format(currentDate, 'dd MMMM yyyy')}
                {filterByLeague && filterCountry && filterLeague && (
                  <> in {filterCountry} - {filterLeague}</>
                )}
              </p>
              
              {nearbyMatchDates.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm text-muted-foreground mb-4">Nearby dates with matches:</p>
                  <div className="flex justify-center space-x-2">
                    {nearbyMatchDates.slice(0, 5).map((date, idx) => (
                      <Button 
                        key={idx}
                        variant="outline"
                        onClick={() => selectDate(date)}
                      >
                        {format(date, 'dd MMM')}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-sm text-muted-foreground mt-4">
                Try selecting a different date
                {filterByLeague && (
                  <> or <button 
                    className="text-primary underline" 
                    onClick={clearLeagueFilter}
                  >
                    view all leagues
                  </button></>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Results;
